/**
 * Published community GHG inventory release (bulk reads).
 *
 * Rows are produced offline by `populate_ghg_inventory.ts` using chart-parity
 * (`fetchGHGInventoryData`). This is the only approved bulk path for rankings,
 * maps, CAP progress, downloads, and statewide rollups.
 *
 * Reads `community_ghg_inventory_release` only. Legacy `ghg_inventory_precalculated`
 * is for explicit comparison tooling, not production bulk paths.
 */
import {
  canonicalInventoryGeofipsKey,
  inventoryGeofipsNumeric,
  preferNewerPrecalcRow,
} from './ghgInventoryGeofipsCanonical';
import {
  COMMUNITY_GHG_RELEASE_META_RELATION,
  COMMUNITY_GHG_RELEASE_RELATION,
} from './ghgInventoryReleaseConstants';
import { fetchPrecalcRowsAsRelease } from './communityGhgInventoryReleasePrecalcFallback';
import { supabaseClient } from './supabase';

export { COMMUNITY_GHG_RELEASE_RELATION };

const PAGE_SIZE = 1000;

/** null = unknown; false = use precalc fallback (table not migrated). */
let releaseRelationAvailable: boolean | null = null;

function isMissingReleaseRelation(error: { message?: string; code?: string }): boolean {
  const msg = error.message ?? '';
  return error.code === '42P01' || msg.includes('does not exist') || msg.includes('community_ghg_inventory_release');
}

async function probeReleaseRelation(): Promise<boolean> {
  if (releaseRelationAvailable !== null) return releaseRelationAvailable;
  const { error } = await supabaseClient.from(COMMUNITY_GHG_RELEASE_RELATION).select('geofips').limit(1);
  if (!error) {
    releaseRelationAvailable = true;
    return true;
  }
  if (isMissingReleaseRelation(error)) {
    releaseRelationAvailable = false;
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        '[GHG] community_ghg_inventory_release is missing; Analysis bulk charts use ghg_inventory_precalculated until you apply sql/create_community_ghg_inventory_release.sql and run npm run populate-ghg-inventory.'
      );
    }
    return false;
  }
  releaseRelationAvailable = true;
  return true;
}

export function clearCommunityGhgReleaseRelationProbe(): void {
  releaseRelationAvailable = null;
}

export interface CommunityGhgReleaseMetadata {
  releaseId: string;
  calculationVersion: string | null;
  releasedAt: string | null;
  sourceDataCutoff: string | null;
  factorMethodVersion: string | null;
  geographicUniverse: string | null;
  rowCount: number | null;
  rowCountSampled: number;
  sourceRelation: string;
}

export interface ReleaseSectorRow {
  geofips: number;
  geofipsRaw: unknown;
  location_name: string;
  geography_type: string;
  year: number;
  calculation_date: string | null;
  calculation_version: string | null;
  total_emissions: number | null;
  ldv_emissions: number | null;
  hdv_emissions: number | null;
  elec_total_emissions: number | null;
  elec_residential_emissions: number | null;
  elec_commercial_emissions: number | null;
  ng_total_emissions: number | null;
  ng_residential_emissions: number | null;
  ng_commercial_emissions: number | null;
  waste_emissions: number | null;
  offroad_emissions: number | null;
  agriculture_emissions: number | null;
  elec_residential_kwh: number | null;
  elec_commercial_kwh: number | null;
  ng_residential_therms: number | null;
  ng_commercial_therms: number | null;
  ldv_vmt: number | null;
}

let cachedReleaseMeta: CommunityGhgReleaseMetadata | null = null;

function numNull(v: unknown): number | null {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export async function fetchCommunityGhgReleaseMetadata(): Promise<CommunityGhgReleaseMetadata> {
  if (cachedReleaseMeta) return cachedReleaseMeta;

  if (!(await probeReleaseRelation())) {
    cachedReleaseMeta = {
      releaseId: 'precalc-fallback',
      calculationVersion: 'ghg_inventory_precalculated',
      releasedAt: null,
      sourceDataCutoff: null,
      factorMethodVersion: null,
      geographicUniverse: null,
      rowCount: null,
      rowCountSampled: 0,
      sourceRelation: 'ghg_inventory_precalculated',
    };
    return cachedReleaseMeta;
  }

  const { data: metaRows, error: metaError } = await supabaseClient
    .from(COMMUNITY_GHG_RELEASE_META_RELATION)
    .select(
      'release_id,calculation_version,calculation_timestamp,source_data_cutoff,factor_method_version,geographic_universe,row_count'
    )
    .eq('is_current', true)
    .limit(1);

  if (!metaError && metaRows?.length) {
    const m = metaRows[0] as Record<string, unknown>;
    cachedReleaseMeta = {
      releaseId: String(m.release_id),
      calculationVersion: m.calculation_version != null ? String(m.calculation_version) : null,
      releasedAt: m.calculation_timestamp != null ? String(m.calculation_timestamp) : null,
      sourceDataCutoff: m.source_data_cutoff != null ? String(m.source_data_cutoff) : null,
      factorMethodVersion: m.factor_method_version != null ? String(m.factor_method_version) : null,
      geographicUniverse: m.geographic_universe != null ? String(m.geographic_universe) : null,
      rowCount: m.row_count != null ? Number(m.row_count) : null,
      rowCountSampled: 1,
      sourceRelation: COMMUNITY_GHG_RELEASE_RELATION,
    };
    return cachedReleaseMeta;
  }

  const { data, error } = await supabaseClient
    .from(COMMUNITY_GHG_RELEASE_RELATION)
    .select('release_id,calculation_date,calculation_version')
    .order('calculation_date', { ascending: false })
    .limit(500);
  if (error) {
    return {
      releaseId: 'unavailable',
      calculationVersion: null,
      releasedAt: null,
      sourceDataCutoff: null,
      factorMethodVersion: null,
      geographicUniverse: null,
      rowCount: null,
      rowCountSampled: 0,
      sourceRelation: COMMUNITY_GHG_RELEASE_RELATION,
    };
  }
  const rows = data ?? [];
  let latestDate: string | null = null;
  let version: string | null = null;
  let releaseId: string | null = null;
  for (const r of rows) {
    const d = r.calculation_date != null ? String(r.calculation_date) : null;
    if (d && (!latestDate || d > latestDate)) {
      latestDate = d;
      version = r.calculation_version != null ? String(r.calculation_version) : version;
      releaseId = r.release_id != null ? String(r.release_id) : releaseId;
    }
  }
  cachedReleaseMeta = {
    releaseId: releaseId ?? latestDate ?? version ?? 'unknown',
    calculationVersion: version,
    releasedAt: latestDate,
    sourceDataCutoff: null,
    factorMethodVersion: null,
    geographicUniverse: null,
    rowCount: null,
    rowCountSampled: rows.length,
    sourceRelation: COMMUNITY_GHG_RELEASE_RELATION,
  };
  return cachedReleaseMeta;
}

export function clearCommunityGhgReleaseMetadataCache(): void {
  cachedReleaseMeta = null;
  clearCommunityGhgReleaseRelationProbe();
}

export type ReleaseGeographyFilter = 'City' | 'County' | 'State';

export interface FetchReleaseRowsOptions {
  year?: number;
  yearMin?: number;
  yearMax?: number;
  geographyType: ReleaseGeographyFilter;
  geofipsIn?: number[];
  select?: string;
}

const DEFAULT_SECTOR_SELECT =
  'geofips,location_name,geography_type,year,calculation_date,calculation_version,total_emissions,ldv_emissions,hdv_emissions,elec_total_emissions,elec_residential_emissions,elec_commercial_emissions,ng_total_emissions,ng_residential_emissions,ng_commercial_emissions,waste_emissions,offroad_emissions,agriculture_emissions,elec_residential_kwh,elec_commercial_kwh,ng_residential_therms,ng_commercial_therms,ldv_vmt';

/**
 * Paginated read of published release rows. O(pages) Supabase requests, not O(cities) live calcs.
 */
export async function fetchCommunityGhgReleaseRows(
  options: FetchReleaseRowsOptions
): Promise<{ rows: ReleaseSectorRow[]; error: string | null }> {
  if (!(await probeReleaseRelation())) {
    return fetchPrecalcRowsAsRelease(options);
  }

  const select = options.select ?? DEFAULT_SECTOR_SELECT;
  const raw: ReleaseSectorRow[] = [];
  let from = 0;
  const meta = await fetchCommunityGhgReleaseMetadata();
  const activeReleaseId = meta.releaseId !== 'unavailable' ? meta.releaseId : null;

  for (;;) {
    let q = supabaseClient.from(COMMUNITY_GHG_RELEASE_RELATION).select(select).order('geofips', {
      ascending: true,
    });
    if (activeReleaseId) {
      q = q.eq('release_id', activeReleaseId);
    }

    if (options.year != null) q = q.eq('year', options.year);
    if (options.yearMin != null) q = q.gte('year', options.yearMin);
    if (options.yearMax != null) q = q.lte('year', options.yearMax);

    if (options.geofipsIn?.length) {
      q = q.in('geofips', options.geofipsIn);
    } else if (options.geographyType === 'County') {
      q = q.eq('geography_type', 'County').gte('geofips', 6000).lt('geofips', 7000);
    } else if (options.geographyType === 'City') {
      q = q.eq('geography_type', 'City').gte('geofips', 600000).lt('geofips', 700000);
    } else {
      q = q.eq('geography_type', 'State');
    }

    const { data, error } = await q.range(from, from + PAGE_SIZE - 1);
    if (error) return { rows: [], error: error.message };
    const batch = (data ?? []) as unknown as Record<string, unknown>[];
    if (batch.length === 0) break;

    for (const r of batch) {
      const gf = inventoryGeofipsNumeric(r.geofips);
      if (gf == null) continue;
      raw.push({
        geofips: gf,
        geofipsRaw: r.geofips,
        location_name: String(r.location_name || '').trim(),
        geography_type: String(r.geography_type || ''),
        year: Number(r.year),
        calculation_date: r.calculation_date != null ? String(r.calculation_date) : null,
        calculation_version: r.calculation_version != null ? String(r.calculation_version) : null,
        total_emissions: numNull(r.total_emissions),
        ldv_emissions: numNull(r.ldv_emissions),
        hdv_emissions: numNull(r.hdv_emissions),
        elec_total_emissions: numNull(r.elec_total_emissions),
        elec_residential_emissions: numNull(r.elec_residential_emissions),
        elec_commercial_emissions: numNull(r.elec_commercial_emissions),
        ng_total_emissions: numNull(r.ng_total_emissions),
        ng_residential_emissions: numNull(r.ng_residential_emissions),
        ng_commercial_emissions: numNull(r.ng_commercial_emissions),
        waste_emissions: numNull(r.waste_emissions),
        offroad_emissions: numNull(r.offroad_emissions),
        agriculture_emissions: numNull(r.agriculture_emissions),
        elec_residential_kwh: numNull(r.elec_residential_kwh),
        elec_commercial_kwh: numNull(r.elec_commercial_kwh),
        ng_residential_therms: numNull(r.ng_residential_therms),
        ng_commercial_therms: numNull(r.ng_commercial_therms),
        ldv_vmt: numNull(r.ldv_vmt),
      });
    }
    if (batch.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }

  return { rows: raw, error: null };
}

/** Dedupe canonical geofips per year (newest calculation_date). */
export function dedupeReleaseRowsByCanonYear(
  rows: ReleaseSectorRow[]
): Map<string, ReleaseSectorRow> {
  const keyed = new Map<string, ReleaseSectorRow>();
  for (const r of rows) {
    const canon = canonicalInventoryGeofipsKey(r.geofipsRaw);
    if (!canon || !Number.isFinite(r.year)) continue;
    const key = `${canon}|${r.year}`;
    const prev = keyed.get(key);
    if (!prev) keyed.set(key, r);
    else {
      keyed.set(
        key,
        preferNewerPrecalcRow(
          { ...prev, calculation_date: prev.calculation_date },
          { ...r, calculation_date: r.calculation_date }
        ) as ReleaseSectorRow
      );
    }
  }
  return keyed;
}
